if (!customElements.get('product-info')) {
  // Recent answers to this section's variant requests, newest last, so a choice already seen (or prefetched while
  // the pointer rested on it) shows at once. Only successful section answers are kept, for a minute at most (stock and
  // price move), ten at most, and none survives a cart change (the quantity field counts what is in the cart). Whole
  // pages, asked for when a combined listing swaps product, are never kept. A request already on its way is shared
  // rather than sent twice. In the theme editor nothing is kept, since every setting changes the answer; a section
  // reloaded there also drops the requests still on their way, which carry its old settings.
  const answers = sectionAnswers({
    size: 10,
    life: 60000,
    clearOn: PUB_SUB_EVENTS.cartUpdate,
    keep: (url) => url.includes('section_id='),
  });
  // The quantity rules count the cart as it is when asked, so none is kept; a request on its way is shared.
  const rulesAnswers = sectionAnswers({ size: 0 });

  class HTMLUpdateUtility {
    /**
     * Swaps a node for the same node of a newer render. The new node takes the old one's place at once, so the page
     * never holds both and no id is twice in it; the callbacks run on the new content before it is inserted and on the
     * new node after.
     */
    static swap(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement('div');
      HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
      const newNode = newNodeWrapper.firstChild;

      oldNode.replaceWith(newNode);

      postProcessCallbacks?.forEach((callback) => callback(newNode));
    }

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using
    // element.innerHTML.
    static setInnerHTML(element, html) {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  }

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);

        this.dispatchProductSelectEvent();

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      });
    }

    getAllSelectedOptions() {
      const options = [];
      this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
        const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
        if (checked) {
          options.push({ name: checked.dataset.optionName || '', value: checked.value });
        }
      });
      return options;
    }

    dispatchProductSelectEvent() {
      const { ProductSelectEvent } = window.StandardEvents || {};
      if (!ProductSelectEvent) return;

      // A newer choice supersedes the one still loading, whose answer is never shown. Its promise settles all the
      // same, rejected as aborted (the contract allows a rejection); its event's promise is already observed.
      this.rejectPendingSelectPromise(new DOMException('A newer choice replaced this one', 'AbortError'));
      const deferred = ProductSelectEvent.createPromise();
      this.pendingSelectPromise = deferred;

      const event = new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      });
      // A failed lookup rejects the event's promise, as its contract asks
      // (https://shopify.dev/docs/api/storefront-events-and-actions/events/product-select). The event hands listeners a
      // promise of its own, derived from this one; listeners are optional and the page shows the failure itself, so
      // that promise is observed here and a failure is not reported as an unhandled rejection.
      event.promise?.catch(() => {});
      this.dispatchEvent(event);
    }

    takePendingSelectPromise() {
      const deferred = this.pendingSelectPromise;
      this.pendingSelectPromise = null;
      return deferred;
    }

    resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
      const deferred = this.takePendingSelectPromise();
      if (!deferred) return;

      if (variant) {
        deferred.resolve({
          variant: {
            id: variant.id,
            title: variant.title,
            availableForSale: variant.available,
            price: {
              amount: sourceVariantSelects?.dataset.selectedPriceAmount,
              currencyCode: sourceVariantSelects?.dataset.currencyCode,
            },
            selectedOptions: this.getAllSelectedOptions(),
          },
        });
      } else {
        deferred.resolve({ variant: null });
      }
    }

    rejectPendingSelectPromise(error) {
      this.takePendingSelectPromise()?.reject(error);
    }

    updateSelectionMetadata({ target }) {
      const { value, tagName } = target;

      if (tagName === 'SELECT' && target.selectedOptions.length) {
        Array.from(target.options)
          .find((option) => option.getAttribute('selected'))
          .removeAttribute('selected');
        target.selectedOptions[0].setAttribute('selected', 'selected');

        // The swatch beside the dropdown holds one swatch per option, in the same order; show the chosen one.
        const preview = target.closest('.product-form__input').querySelector('.dropdown-swatch');
        if (!preview) return;
        Array.from(preview.children).forEach((swatch, index) => {
          swatch.hidden = index !== target.selectedIndex;
        });
      } else if (tagName === 'INPUT' && target.type === 'radio') {
        const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
        if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      }
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }
  }

  customElements.define('variant-selects', VariantSelects);

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [
        () => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        },
      ];

      constructor() {
        super();

        this.quantityInput = this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.rememberSelection();
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.addEventListener('pointerover', (event) => this.onPointerOver(event), { signal });
        this.addEventListener(
          'click',
          (event) => {
            if (!event.target.closest(`#VariantError-${this.dataset.section} .notice__action`)) return;
            this.showLoadError(false);
            if (this.lastAttempt) this.renderProductInfo(this.lastAttempt);
          },
          { signal }
        );
      }

      // The picker and the add to cart button as the server last rendered them, to come back to when a
      // variant request fails.
      rememberSelection() {
        this.confirmedPicker = this.variantSelectors?.innerHTML ?? null;
        const button = this.querySelector('[id^="ProductSubmitButton-"]');
        this.confirmedButton = button
          ? {
              disabled: button.hasAttribute('disabled'),
              text: button.querySelector('span')?.textContent ?? '',
            }
          : null;
      }

      // Resting the pointer on a value for a moment loads the section for the choice it would make.
      onPointerOver(event) {
        if (event.pointerType !== 'mouse') return;
        const input = this.inputForPrefetch(event.target);
        if (!input || input === this.prefetchInput) return;
        this.prefetchInput = input;
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = setTimeout(() => {
          if (input.checked || (!input.matches(':hover') && !input.labels?.[0]?.matches(':hover'))) return;
          const url = this.prefetchUrl(input);
          if (url) answers.get(url).catch(() => {});
        }, 250);
      }

      inputForPrefetch(target) {
        const selects = this.variantSelectors;
        if (!selects?.contains(target)) return null;
        const label = target.closest('label');
        const input = label?.htmlFor ? selects.querySelector(`#${CSS.escape(label.htmlFor)}`) : target.closest('input');
        if (!input || input.type !== 'radio' || input.checked || input.dataset.productUrl) return null;
        return input;
      }

      prefetchUrl(input) {
        const values = Array.from(
          this.variantSelectors.querySelectorAll('select option[selected], fieldset input:checked')
        ).map((element) =>
          element.name === input.name && element.tagName === 'INPUT'
            ? input.dataset.optionValueId
            : element.dataset.optionValueId
        );
        if (values.some((value) => !value)) return null;
        return this.buildRequestUrlWithParams(this.dataset.url, values);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.controller?.abort();
        clearTimeout(this.prefetchTimer);
        // A choice or the quantity rules still loading when the section goes (closed quick add, editor reload) are
        // never shown.
        this.abortController?.abort();
        this.rulesController?.abort();
        this.variantSelectors?.rejectPendingSelectPromise(
          new DOMException('The product section was removed', 'AbortError')
        );
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
          expect: shouldFetchFullPage ? "product-info[id^='MainProduct']" : 'product-info',
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.swap(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.swap(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, expect = 'product-info' }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.lastAttempt = { requestUrl, targetId, callback, expect };
        // Only the latest choice counts: an older response that still arrives is left alone.
        const ticket = (this.ticket = (this.ticket ?? 0) + 1);
        // What the answers hand back (kept, shared or new) belongs to the current cart: older ones are dropped when
        // the cart changes.
        const generation = answers.generation;

        answers
          .page(requestUrl, this.abortController.signal)
          .then((html) => {
            if (ticket !== this.ticket) return;
            // The cart changed while the answer was on its way, so its quantity rules count the old cart (and would
            // undo the count fetchQuantityRules has just shown): the same choice is asked for again.
            if (generation !== answers.generation) {
              if (this.isConnected) this.renderProductInfo({ requestUrl, targetId, callback, expect });
              return;
            }
            this.pendingRequestUrl = null;
            if (!html.querySelector(expect)) {
              answers.drop(requestUrl);
              throw new Error('The response holds no product section');
            }
            callback(html);
            this.showLoadError(false);
            this.rememberSelection();
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError' || ticket !== this.ticket) return;
            console.warn('Core: the variant could not be loaded', error);
            this.pendingRequestUrl = null;
            this.variantSelectors?.rejectPendingSelectPromise(error);
            this.restoreSelection();
            this.showLoadError(true);
          });
      }

      // Back to the last choice the page shows, with its button, after a failed request.
      restoreSelection() {
        if (this.variantSelectors && this.confirmedPicker !== null) {
          this.variantSelectors.innerHTML = this.confirmedPicker;
        }
        if (this.confirmedButton) {
          this.productForm?.toggleSubmitButton(this.confirmedButton.disabled, this.confirmedButton.text);
        }
      }

      // The message under the picker is written hidden, with its retry button (snippets/variant-error.liquid).
      showLoadError(show) {
        const notice = this.querySelector(`#VariantError-${this.dataset.section}`);
        if (notice) notice.hidden = !show;
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.swap(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            // Parts that do not need a variant (the title, rows) still take the server's version of the page.
            this.updateVariantParts(html);
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.updateVariantParts(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.updateSubmitButton(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      // Each element marked data-variant-part="Key" (with the id Key-<section id>) takes its content and
      // its hidden state from the same element of the section rendered for the new variant. Rows that were open stay
      // open. Those also marked data-needs-variant hide when the chosen values match no variant (setUnavailable).
      updateVariantParts(html) {
        this.querySelectorAll('[data-variant-part]').forEach((destination) => {
          const source = html.getElementById(`${destination.dataset.variantPart}-${this.sectionId}`);
          if (!source) return;
          const wasOpen = destination.querySelector('details')?.open;
          destination.innerHTML = source.innerHTML;
          if (wasOpen !== undefined)
            destination.querySelectorAll('details').forEach((details) => (details.open = wasOpen));
          destination.classList.toggle('hidden', source.classList.contains('hidden'));
        });
        this.dispatchEvent(new CustomEvent('product-info:parts-updated'));
      }

      // The button reads and works as the server rendered it for the new variant.
      updateSubmitButton(html) {
        const source = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const disabled = source?.hasAttribute('disabled') ?? true;
        const text = source?.querySelector('span')?.textContent.trim() || themeStrings('soldOut');
        this.productForm?.toggleSubmitButton(disabled, text);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, themeStrings('unavailable'));

        // Only the parts that describe a variant (price, stock, identifiers, badges, delivery) hide, with the quantity
        // rules and the volume pricing note.
        const section = this.dataset.section;
        this.querySelectorAll(`#Quantity-Rules-${section}, #Volume-Note-${section}, [data-needs-variant]`).forEach(
          ({ classList }) => classList.add('hidden')
        );
      }

      // The page's gallery, not the lightbox's (snippets/product-media-modal.liquid), which copies it as it opens.
      updateMedia(html, variantFeaturedMediaId) {
        const galleryId = `MediaGallery-${this.dataset.section}`;
        const gallery = this.querySelector(`#${CSS.escape(galleryId)}`);
        if (!gallery) return;
        gallery.syncWith?.(html.querySelector(`#${CSS.escape(galleryId)}`));
        const mediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : gallery.querySelector('.product__media-item:not(.hidden)')?.dataset.mediaId;
        if (mediaId) gallery.setActiveMedia?.(mediaId, { instant: true, reveal: true });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        // The count in the cart is busy while it is asked for. The answer swaps its markup, so it is looked up again
        // when the work ends.
        const count = () => this.querySelector('.quantity__rules-cart');
        count()?.setAttribute('aria-busy', 'true');
        // A newer cart change asks again, so the answer still on its way is dropped.
        this.rulesController?.abort();
        const controller = (this.rulesController = new AbortController());
        return rulesAnswers
          .page(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, controller.signal)
          .then((html) => {
            // Another variant was chosen meanwhile. Its own answer counts the cart (one from before the cart
            // changed is asked for again), so this one, for the variant shown before, is left alone.
            if (this.productForm?.variantIdInput?.value !== currentVariantId) return;
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.warn('Core: the quantity rules could not be loaded', error);
          })
          .finally(() => {
            if (this.rulesController === controller) count()?.removeAttribute('aria-busy');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['quantity-input input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === 'quantity-input input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.section;
      }
    }
  );
}
